import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Heart,
  Stethoscope,
  ThermometerSun,
  Scale,
  Brain,
  Loader2,
  Plus,
  History,
  Sparkles,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const urgencyColors = {
  routine: 'bg-green-100 text-green-700',
  soon: 'bg-yellow-100 text-yellow-700',
  urgent: 'bg-orange-100 text-orange-700',
  emergency: 'bg-red-100 text-red-700'
};

const severityColors = {
  mild: 'text-green-600',
  moderate: 'text-yellow-600',
  severe: 'text-red-600'
};

const HealthDetection = () => {
  const { user } = useAuth();
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [symptoms, setSymptoms] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);

  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [checkForm, setCheckForm] = useState({
    weight: '',
    temperature: '',
    appetite: 'normal',
    energy_level: 'normal',
    stool_quality: 'normal',
    water_intake: 'normal',
    behavioral_changes: '',
    visible_issues: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (selectedPet) {
      fetchPetData(selectedPet.id);
    }
  }, [selectedPet]);

  const fetchData = async () => {
    try {
      const [petsRes, symptomsRes] = await Promise.all([
        user ? axios.get(`${API}/api/pets/my-pets`) : Promise.resolve({ data: [] }),
        axios.get(`${API}/api/health/symptoms`)
      ]);
      setPets(petsRes.data);
      setSymptoms(symptomsRes.data);
      if (petsRes.data.length > 0) {
        setSelectedPet(petsRes.data[0]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPetData = async (petId) => {
    try {
      const [analysesRes, checksRes] = await Promise.all([
        axios.get(`${API}/api/health/analyses/${petId}`),
        axios.get(`${API}/api/health/checks/${petId}`)
      ]);
      setAnalyses(analysesRes.data);
      setHealthChecks(checksRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAnalyze = async () => {
    if (selectedSymptoms.length === 0) {
      toast.error('Please select at least one symptom');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/api/health/analyze`, {
        pet_id: selectedPet.id,
        symptoms: selectedSymptoms,
        additional_notes: additionalNotes,
        include_medical_history: true
      });
      setCurrentAnalysis(res.data);
      setAnalyzeDialogOpen(false);
      setSelectedSymptoms([]);
      setAdditionalNotes('');
      fetchPetData(selectedPet.id);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleHealthCheck = async () => {
    try {
      await axios.post(`${API}/api/health/checks`, {
        pet_id: selectedPet.id,
        ...checkForm,
        weight: checkForm.weight ? parseFloat(checkForm.weight) : null,
        temperature: checkForm.temperature ? parseFloat(checkForm.temperature) : null,
        visible_issues: checkForm.visible_issues.split(',').map(i => i.trim()).filter(Boolean)
      });
      toast.success('Health check logged!');
      setCheckDialogOpen(false);
      setCheckForm({
        weight: '',
        temperature: '',
        appetite: 'normal',
        energy_level: 'normal',
        stool_quality: 'normal',
        water_intake: 'normal',
        behavioral_changes: '',
        visible_issues: '',
        notes: ''
      });
      fetchPetData(selectedPet.id);
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const toggleSymptom = (symptom) => {
    if (selectedSymptoms.includes(symptom)) {
      setSelectedSymptoms(selectedSymptoms.filter(s => s !== symptom));
    } else {
      setSelectedSymptoms([...selectedSymptoms, symptom]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <GuestBanner message="Sign up to use AI health detection for your pets!" />
          <Card className="max-w-lg mx-auto text-center py-16 mt-8">
            <Stethoscope className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-heading mb-2">AI Health Detection</h2>
            <p className="text-muted-foreground mb-4">Log in to analyze your pet's health with AI</p>
            <Button onClick={() => window.location.href = '/login'}>Log In</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-primary" />
              AI Health Detection
            </h1>
            <p className="text-muted-foreground">
              Proactive health monitoring with AI-powered analysis
            </p>
          </div>
          <div className="flex gap-3">
            {pets.length > 0 && (
              <Select value={selectedPet?.id} onValueChange={(val) => setSelectedPet(pets.find(p => p.id === val))}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select pet" />
                </SelectTrigger>
                <SelectContent>
                  {pets.map(pet => (
                    <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {pets.length === 0 ? (
          <Card className="text-center py-16">
            <Activity className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl mb-2">No pets registered</h3>
            <p className="text-muted-foreground mb-4">Add a pet to start health monitoring</p>
            <Button onClick={() => window.location.href = '/my-pets'}>Add Pet</Button>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* AI Analysis Card */}
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <Brain className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-medium">AI Symptom Analysis</h3>
                        <p className="text-sm text-muted-foreground">Get AI-powered health insights</p>
                      </div>
                    </div>
                    <Dialog open={analyzeDialogOpen} onOpenChange={setAnalyzeDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" data-testid="analyze-btn">
                          <Sparkles className="w-4 h-4 mr-2" />
                          Analyze Symptoms
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Symptom Analysis for {selectedPet?.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 mt-4">
                          <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            This AI analysis is for informational purposes only. Always consult a veterinarian for medical advice.
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-3">Select observed symptoms:</h4>
                            {Object.entries(symptoms).map(([category, symptomList]) => (
                              <div key={category} className="mb-4">
                                <p className="text-sm text-muted-foreground capitalize mb-2">{category.replace('_', ' ')}</p>
                                <div className="flex flex-wrap gap-2">
                                  {symptomList.map(symptom => (
                                    <Badge
                                      key={symptom}
                                      variant={selectedSymptoms.includes(symptom) ? 'default' : 'outline'}
                                      className="cursor-pointer"
                                      onClick={() => toggleSymptom(symptom)}
                                    >
                                      {symptom}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>

                          <Textarea
                            placeholder="Additional notes (optional)"
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            rows={3}
                          />

                          <Button onClick={handleAnalyze} className="w-full" disabled={analyzing || selectedSymptoms.length === 0}>
                            {analyzing ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Brain className="w-4 h-4 mr-2" />
                                Run AI Analysis
                              </>
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>

                {/* Health Check Card */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-heading font-medium">Log Health Check</h3>
                        <p className="text-sm text-muted-foreground">Track vitals and observations</p>
                      </div>
                    </div>
                    <Dialog open={checkDialogOpen} onOpenChange={setCheckDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" data-testid="health-check-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Log Health Check
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Health Check for {selectedPet?.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm">Weight (lbs)</label>
                              <Input type="number" value={checkForm.weight} onChange={(e) => setCheckForm({...checkForm, weight: e.target.value})} />
                            </div>
                            <div>
                              <label className="text-sm">Temperature (°F)</label>
                              <Input type="number" step="0.1" value={checkForm.temperature} onChange={(e) => setCheckForm({...checkForm, temperature: e.target.value})} />
                            </div>
                          </div>
                          {['appetite', 'energy_level', 'stool_quality', 'water_intake'].map(field => (
                            <div key={field}>
                              <label className="text-sm capitalize">{field.replace('_', ' ')}</label>
                              <Select value={checkForm[field]} onValueChange={(val) => setCheckForm({...checkForm, [field]: val})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="normal">Normal</SelectItem>
                                  <SelectItem value="increased">Increased</SelectItem>
                                  <SelectItem value="decreased">Decreased</SelectItem>
                                  {field === 'appetite' && <SelectItem value="none">None</SelectItem>}
                                  {field === 'energy_level' && <SelectItem value="lethargic">Lethargic</SelectItem>}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                          <Input placeholder="Visible issues (comma-separated)" value={checkForm.visible_issues} onChange={(e) => setCheckForm({...checkForm, visible_issues: e.target.value})} />
                          <Textarea placeholder="Notes" value={checkForm.notes} onChange={(e) => setCheckForm({...checkForm, notes: e.target.value})} rows={2} />
                          <Button onClick={handleHealthCheck} className="w-full">Save Health Check</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </div>

              {/* Latest Analysis Result */}
              {currentAnalysis && (
                <Card className="border-primary/20">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-primary" />
                        Latest Analysis Results
                      </CardTitle>
                      <Badge className={urgencyColors[currentAnalysis.urgency_level]}>
                        {currentAnalysis.urgency_level}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Symptoms: {currentAnalysis.symptoms_analyzed.join(', ')}</span>
                    </div>

                    {currentAnalysis.identified_risks.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Identified Risks</h4>
                        <div className="space-y-3">
                          {currentAnalysis.identified_risks.map((risk) => (
                            <div key={risk.condition} className="p-3 bg-muted rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{risk.condition}</span>
                                <div className="flex gap-2">
                                  <Badge variant="outline">{risk.likelihood} likelihood</Badge>
                                  <Badge className={severityColors[risk.severity]}>{risk.severity}</Badge>
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{risk.description}</p>
                              {risk.recommended_actions.length > 0 && (
                                <ul className="text-sm list-disc list-inside">
                                  {risk.recommended_actions.map((action) => (
                                    <li key={action}>{action}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentAnalysis.general_health_tips.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Health Tips</h4>
                        <ul className="text-sm space-y-1">
                          {currentAnalysis.general_health_tips.map((tip) => (
                            <li key={tip} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {currentAnalysis.recommended_vet_visit && (
                      <div className="p-3 bg-amber-50 rounded-lg flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="text-sm font-medium">Veterinary visit recommended</span>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground italic">{currentAnalysis.disclaimer}</p>
                  </CardContent>
                </Card>
              )}

              {/* Analysis History */}
              <Tabs defaultValue="analyses">
                <TabsList>
                  <TabsTrigger value="analyses">AI Analyses ({analyses.length})</TabsTrigger>
                  <TabsTrigger value="checks">Health Checks ({healthChecks.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="analyses" className="space-y-4 mt-4">
                  {analyses.length === 0 ? (
                    <Card className="text-center py-8">
                      <Brain className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-muted-foreground">No analyses yet</p>
                    </Card>
                  ) : (
                    analyses.map(analysis => (
                      <Card key={analysis.id} className="cursor-pointer hover:shadow-md" onClick={() => setCurrentAnalysis(analysis)}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{new Date(analysis.analysis_date).toLocaleDateString()}</p>
                              <p className="font-medium">{analysis.symptoms_analyzed.slice(0, 3).join(', ')}{analysis.symptoms_analyzed.length > 3 ? '...' : ''}</p>
                              <p className="text-sm">{analysis.identified_risks.length} risk(s) identified</p>
                            </div>
                            <Badge className={urgencyColors[analysis.urgency_level]}>{analysis.urgency_level}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
                <TabsContent value="checks" className="space-y-4 mt-4">
                  {healthChecks.length === 0 ? (
                    <Card className="text-center py-8">
                      <Activity className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-muted-foreground">No health checks yet</p>
                    </Card>
                  ) : (
                    healthChecks.map(check => (
                      <Card key={check.id}>
                        <CardContent className="p-4">
                          <p className="text-sm text-muted-foreground mb-2">{new Date(check.created_at).toLocaleDateString()}</p>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            {check.weight && <div><Scale className="w-4 h-4 inline mr-1" />{check.weight} lbs</div>}
                            {check.temperature && <div><ThermometerSun className="w-4 h-4 inline mr-1" />{check.temperature}°F</div>}
                            <div>Appetite: {check.appetite}</div>
                            <div>Energy: {check.energy_level}</div>
                          </div>
                          {check.notes && <p className="text-sm text-muted-foreground mt-2">{check.notes}</p>}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Pet Info Sidebar */}
            <div className="space-y-4">
              {selectedPet && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedPet.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm">
                      <p><strong>Species:</strong> {selectedPet.species}</p>
                      <p><strong>Breed:</strong> {selectedPet.breed || 'Unknown'}</p>
                      <p><strong>Age:</strong> {selectedPet.age || 'Unknown'}</p>
                      <p><strong>Weight:</strong> {selectedPet.weight ? `${selectedPet.weight} lbs` : 'Unknown'}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Heart className="w-5 h-5 text-red-500" />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Total Analyses</span>
                    <Badge variant="secondary">{analyses.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Health Checks</span>
                    <Badge variant="secondary">{healthChecks.length}</Badge>
                  </div>
                  {healthChecks.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Last Check</span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(healthChecks[0]?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthDetection;
